/**
 * Creating a party.
 *
 * Built against the reference (exec-0ce66fa9). The essential choices come
 * first - game, name, mode, region, size, who can see it, microphone - and the
 * rest sits behind More options. A live preview shows the card other players
 * will see, because "what will this look like" is the question people answer
 * by posting and then editing.
 *
 * What the old dialog got wrong, and what replaced it:
 *
 *   - Game names were cut to "Counter-…" in a 250px scroller nested inside the
 *     scrolling dialog. Tiles now carry full names, saved games come first,
 *     search finds the rest, and there is one scroll surface.
 *   - A failed submit was a toast that vanished. Errors now sit in the form,
 *     every entry is kept, and the common one - already being in a party -
 *     offers the way to that party.
 *   - Size said "up to N" without explaining N. It still does, but says what
 *     the plan limit is and that very large sizes are for social lobbies, not
 *     squads.
 *
 * Validation matches the server exactly: a game is required, the title is at
 * most 60 characters, the description 500, size 2 to the account's cap, and
 * at most six tags. The server clamps an oversized request silently, so the
 * form is what stops anyone asking for more than they can have.
 */

import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { gameBanner } from '@/lib/gameArt';
import { Icon } from '@/icons/Icon';
import { Select } from './Select';
import { Button, Field, Modal, Toggle } from './ui';
import { EmojiField } from './EmojiField';
import { NumberField } from './NumberField';

type Visibility = 'public' | 'friends' | 'invite';

/**
 * A party you post every week, kept.
 *
 * Saved in this browser rather than on the server: it is a convenience for
 * the person filling the form in, not something anybody else ever reads, and
 * it should not need a round trip to offer.
 */
type Template = {
  id: string;
  name: string;
  gameId: string;
  title: string;
  mode: string;
  region: string;
  size: number;
  visibility: Visibility;
  micRequired: boolean;
  language: string;
  styles: string[];
  description: string;
  minRank: string;
  maxRank: string;
  needRoles: string[];
  voice: boolean;
  textEnabled: boolean;
  sessionLength: SessionLength;
};

type SessionLength = '' | 'one' | 'short' | 'long';

/** How long the party expects to be at it. */
const LENGTHS: { id: SessionLength; label: string; note: string }[] = [
  { id: '', label: 'Not sure', note: 'Play it by ear.' },
  { id: 'one', label: 'One match', note: 'In and out. Good for a quick game.' },
  { id: 'short', label: 'About an hour', note: 'A few games and done.' },
  { id: 'long', label: 'Long session', note: 'Settle in. People come and go.' }
];

const TEMPLATE_KEY = 'pogu.party.templates';
const MAX_TEMPLATES = 6;

function readTemplates(): Template[] {
  try {
    const raw = JSON.parse(localStorage.getItem(TEMPLATE_KEY) || '[]');
    return Array.isArray(raw) ? raw.slice(0, MAX_TEMPLATES) : [];
  } catch { return []; }
}

function writeTemplates(list: Template[]) {
  try { localStorage.setItem(TEMPLATE_KEY, JSON.stringify(list.slice(0, MAX_TEMPLATES))); }
  catch { /* storage off - the form still works, it just forgets */ }
}

const VISIBILITY: { id: Visibility; label: string; explain: string }[] = [
  { id: 'public', label: 'Public', explain: 'Listed in Parties and on Home. Anyone with a free seat can join.' },
  { id: 'friends', label: 'Friends only', explain: 'Only your friends see it and can join.' },
  { id: 'invite', label: 'Invite only', explain: 'Hidden from every list. People join from an invite you send.' }
];

/** What people play on. Only asked because of the lobbies it saves joining. */
export const PLATFORMS = [
  { id: 'pc', label: 'PC', icon: 'monitor' },
  { id: 'playstation', label: 'PlayStation', icon: 'gamepad' },
  { id: 'xbox', label: 'Xbox', icon: 'gamepad' },
  { id: 'switch', label: 'Switch', icon: 'gamepad' },
  { id: 'mobile', label: 'Mobile', icon: 'laptop' }
] as const;

/** How many game tiles show before "Show all games". */
const FIRST_GAMES = 8;
const MAX_STYLES = 3;
/** The server keeps six; the form never lets you ask for a seventh. */
const MAX_ROLES = 6;

export function CreateParty({ onClose, seed }: {
  onClose: () => void;
  /** Filled in from somewhere else - a crew event turning into a party. */
  seed?: { gameId?: string; title?: string };
}) {
  const catalog = useSession((s) => s.catalog);
  const me = useSession((s) => s.me);
  const queryClient = useQueryClient();

  const games = catalog?.games ?? [];
  const regions = catalog?.regions ?? [];
  const cap = Math.max(2, me?.partySizeCap ?? 5);

  /* ---- the form ---- */
  const savedIds = useMemo(() => new Set((me?.games ?? []).map((g) => g.id)), [me?.games]);
  const [gameId, setGameId] = useState(() => seed?.gameId || (me?.games?.find((g) => games.some((x) => x.id === g.id))?.id ?? ''));
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [title, setTitle] = useState(seed?.title ?? '');
  const [mode, setMode] = useState('');
  const [region, setRegion] = useState(() =>
    regions.some((r) => r.id === me?.region) ? me!.region : (regions[0]?.id ?? ''));
  const [size, setSize] = useState(() => Math.min(5, cap));
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [micRequired, setMicRequired] = useState(false);
  const [more, setMore] = useState(false);
  const [language, setLanguage] = useState(() => me?.languages?.[0] ?? catalog?.languages?.[0] ?? 'English');
  const [styles, setStyles] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  /* The rest of what the server has always accepted and the form never asked
     for: who you want on the team, how good, and which rooms are open. */
  const [minRank, setMinRank] = useState('');
  const [maxRank, setMaxRank] = useState('');
  const [needRoles, setNeedRoles] = useState<string[]>([]);
  const [voice, setVoice] = useState(true);
  const [textEnabled, setTextEnabled] = useState(true);
  const [sessionLength, setSessionLength] = useState<SessionLength>('');
  const [platforms, setPlatforms] = useState<string[]>(() => me?.platforms ?? []);
  const [newcomers, setNewcomers] = useState(false);
  const [templates, setTemplates] = useState<Template[]>(() => readTemplates());
  const [naming, setNaming] = useState(false);
  const [templateName, setTemplateName] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; inParty: boolean } | null>(null);
  /* A ref, not state: two clicks in the same frame both see busy === false. */
  const inFlight = useRef(false);

  const game = games.find((g) => g.id === gameId);
  const effectiveMode = mode || game?.modes?.[0] || '';
  const ranks = game?.ranks ?? [];
  const roles = game?.roles ?? [];
  const rankRange = minRank || maxRank
    ? (minRank || ranks[0] || 'Any') + ' – ' + (maxRank || ranks[ranks.length - 1] || 'Any')
    : '';

  /* What the folded section is holding, so nobody has to open it to find out. */
  const moreSummary = [
    language,
    styles.length ? styles.join(', ') : '',
    rankRange,
    needRoles.length ? needRoles.length + (needRoles.length === 1 ? ' role' : ' roles') : '',
    description.trim() ? 'a description' : '',
    voice ? '' : 'no voice',
    textEnabled ? '' : 'no chat'
  ].filter(Boolean).join(' · ');

  /* ---- which game tiles to draw ---- */
  const ordered = useMemo(
    () => [...games.filter((g) => savedIds.has(g.id)), ...games.filter((g) => !savedIds.has(g.id))],
    [games, savedIds]
  );
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((g) => (g.name + ' ' + g.short).toLowerCase().includes(q));
  }, [ordered, query]);
  const visibleGames = useMemo(() => {
    if (query.trim() || showAll) return matches;
    const first = matches.slice(0, FIRST_GAMES);
    /* The chosen game is always on screen, even if it sits past the fold. */
    if (game && !first.some((g) => g.id === game.id)) first[first.length - 1] = game;
    return first;
  }, [matches, query, showAll, game]);

  async function create() {
    if (inFlight.current) return;
    if (!gameId) { setError({ message: 'Pick a game to continue.', inParty: false }); return; }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ party: Party }>('/api/parties', {
        gameId,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        size: Math.min(Math.max(2, size), cap),
        mode: effectiveMode || undefined,
        region: region || undefined,
        language,
        micRequired,
        visibility,
        tags: styles,
        sessionLength: sessionLength || undefined,
        platforms,
        newcomers,
        minRank: minRank || undefined,
        maxRank: maxRank || undefined,
        needRoles,
        voice,
        textEnabled
      });
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      onClose();
      go('party/' + res.party.id);
    } catch (err) {
      const e = err as Error & { code?: string };
      /* Everything typed stays where it is. */
      setError({ message: e.message || 'The party was not created. Try again.', inParty: e.code === 'in-party' || /already in/i.test(e.message) });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  /** Fill the whole form in from a saved setup. */
  function applyTemplate(t: Template) {
    setGameId(t.gameId);
    setTitle(t.title);
    setMode(t.mode);
    setRegion(t.region);
    setSize(Math.min(Math.max(2, t.size), cap));
    setVisibility(t.visibility);
    setMicRequired(t.micRequired);
    setLanguage(t.language);
    setStyles(t.styles ?? []);
    setDescription(t.description ?? '');
    setMinRank(t.minRank ?? '');
    setMaxRank(t.maxRank ?? '');
    setNeedRoles(t.needRoles ?? []);
    setVoice(t.voice !== false);
    setTextEnabled(t.textEnabled !== false);
    setSessionLength(t.sessionLength ?? '');
    setError(null);
    if ((t.description || '').trim() || (t.needRoles ?? []).length || t.minRank || t.maxRank) setMore(true);
  }

  /** Keep what is on screen, under a name you will recognise next Friday. */
  function saveTemplate() {
    const name = templateName.trim() || (game ? game.name + ' squad' : 'My setup');
    const next: Template = {
      id: 't' + Date.now().toString(36),
      name, gameId, title, mode: effectiveMode, region,
      size, visibility, micRequired, language, styles, description,
      minRank, maxRank, needRoles, voice, textEnabled, sessionLength
    };
    /* Same name means replacing it - saving "Friday" four times should leave
       one Friday, not four. */
    const list = [next, ...templates.filter((t) => t.name.toLowerCase() !== name.toLowerCase())].slice(0, MAX_TEMPLATES);
    setTemplates(list);
    writeTemplates(list);
    setNaming(false);
  }

  function goToCurrentParty() {
    const mine = queryClient.getQueryData<{ parties: Party[] }>(['parties', 'mine']);
    const current = mine?.parties?.find((p) => !p.closed);
    onClose();
    go(current ? 'party/' + current.id : 'parties');
  }

  return (
    <Modal
      xl
      title="Create a party"
      subtitle="Pick your game. Find your people."
      onClose={onClose}
      footer={
        <>
          {gameId ? (
            naming ? (
              <span className="mr-auto flex items-center gap-1.5">
                <input
                  autoFocus
                  data-enter-own
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value.slice(0, 24))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveTemplate(); } if (e.key === 'Escape') { e.preventDefault(); setNaming(false); } }}
                  placeholder="Friday casual"
                  className="h-9 w-[150px] rounded-[10px] border border-line bg-surface-1 px-2.5 text-[13px] outline-none focus:border-accent"
                />
                <Button size="sm" kind="outline" onClick={saveTemplate}>Save</Button>
              </span>
            ) : (
              <Button
                size="sm"
                kind="plain"
                className="mr-auto"
                onClick={() => { setTemplateName(game ? game.name + ' squad' : 'My setup'); setNaming(true); }}
              >
                Save these settings
              </Button>
            )
          ) : null}
          <Button kind="outline" onClick={onClose}>Cancel</Button>
          <Button data-primary kind="primary" disabled={busy} onClick={() => void create()}>
            {busy ? 'Creating…' : 'Create party'}
          </Button>
        </>
      }
    >
      <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_260px]">
        {/* ============================ the form ============================ */}
        <div className="flex min-w-0 flex-col gap-5">
          {/* ---- saved setups ---- */}
          {templates.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[12.5px] font-semibold text-mute">Saved:</span>
              {templates.map((t) => (
                <span key={t.id} className="flex items-center rounded-full bg-surface-2 pr-1 pl-3 text-[12.5px] font-semibold text-dim">
                  <button
                    type="button"
                    onClick={() => applyTemplate(t)}
                    className="py-1.5 outline-none transition-colors hover:text-ink focus-visible:text-accent"
                  >
                    {t.name}
                  </button>
                  <button
                    type="button"
                    aria-label={'Forget ' + t.name}
                    onClick={() => {
                      const next = templates.filter((x) => x.id !== t.id);
                      setTemplates(next);
                      writeTemplates(next);
                    }}
                    className="ml-1 grid h-6 w-6 place-items-center rounded-full text-ghost outline-none transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          {error ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] border border-[color-mix(in_srgb,var(--bad)_30%,transparent)] bg-[color-mix(in_srgb,var(--bad)_7%,var(--bg-1))] px-4 py-3">
              <p className="m-0 min-w-0 flex-1 text-[13.5px] text-ink">{error.message}</p>
              {error.inParty ? <Button size="sm" kind="outline" onClick={goToCurrentParty}>Go to your party</Button> : null}
            </div>
          ) : null}

          {/* ---- game ---- */}
          <fieldset className="m-0 min-w-0 border-0 p-0">
            <legend className="mb-2 text-[13.5px] font-semibold text-ink">Game</legend>
            <div className="relative mb-3">
              <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for a game…"
                aria-label="Search games"
                spellCheck={false}
                className={cn(
                  'h-[var(--h-md)] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-inset pr-3 pl-10 text-[14px] text-ink outline-none',
                  'placeholder:text-ghost transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'hover:border-line focus:border-accent focus:shadow-[0_0_0_3px_var(--accent-soft)]'
                )}
              />
            </div>

            {visibleGames.length ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {visibleGames.map((g) => {
                  const on = g.id === gameId;
                  const art = gameBanner(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => {
                        setGameId(g.id);
                        /* The size this game is actually played at, unless the
                           number on screen was deliberately changed. */
                        const squad = g.squad ?? 5;
                        if (size === (game?.squad ?? 5) || size === 5) setSize(Math.min(Math.max(2, squad), cap));
                        setMode('');
                        setMinRank('');
                        setMaxRank('');
                        setNeedRoles([]);
                        setError(null);
                      }}
                      className={cn(
                        'group flex min-w-0 flex-col gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] p-1 text-left outline-none',
                        'focus-visible:shadow-[0_0_0_2px_var(--accent)]'
                      )}
                    >
                      <span
                        className={cn(
                          'relative block aspect-[16/10] w-full overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] border-2 bg-surface-3',
                          'transition-[border-color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                          on ? 'border-accent shadow-[0_0_0_3px_var(--accent-soft)]' : 'border-transparent group-hover:border-line'
                        )}
                      >
                        {art ? (
                          <img src={art} alt="" loading="lazy" decoding="async" width={200} height={125} className="h-full w-full object-cover" />
                        ) : (
                          <span aria-hidden className="absolute inset-0 grid place-items-center text-[18px] font-extrabold text-mute">{g.short}</span>
                        )}
                        {on ? (
                          <span aria-hidden className="absolute top-1.5 right-1.5 grid h-6 w-6 place-items-center rounded-full bg-accent text-accent-fg shadow-[var(--shadow-1)]">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
                          </span>
                        ) : null}
                        {savedIds.has(g.id) ? <span className="sr-only">, one of your games</span> : null}
                      </span>
                      {/* Two lines, never an ellipsis: "Counter-…" is not a game. */}
                      <span className="line-clamp-2 px-0.5 text-[13px] leading-snug font-semibold text-ink">{g.name}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="m-0 rounded-[calc(12px*var(--radius-mul,1))] border border-dashed border-line px-4 py-6 text-center text-[13.5px] text-mute">
                No game matches “{query.trim()}”.
              </p>
            )}

            {!query.trim() && matches.length > FIRST_GAMES ? (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="mt-2 rounded-[8px] px-1 py-1 text-[13px] font-semibold text-accent outline-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:text-accent-hi focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                {showAll ? 'Show fewer games' : 'Show all ' + matches.length + ' games'}
              </button>
            ) : null}
          </fieldset>

          {/* ---- name ---- */}
          <Field label="Party name" hint={(title.length ? title.length + '/60 · ' : '') + 'Left blank, it is called “' + (game ? game.name + ' squad' : 'Game squad') + '”.'}>
            <EmojiField
              value={title}
              onChange={setTitle}
              maxLength={60}
              placeholder={game ? game.name + ' squad' : 'A relaxed evening squad'}
            />
          </Field>

          {/* ---- how long ---- */}
          <div>
            <span id="cp-length" className="mb-2 block text-[13.5px] font-semibold text-ink">How long are you playing?</span>
            <div role="radiogroup" aria-labelledby="cp-length" className="flex flex-wrap gap-1.5">
              {LENGTHS.map((l) => {
                const on = l.id === sessionLength;
                return (
                  <button
                    key={l.id || 'any'}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => setSessionLength(l.id)}
                    className={cn(
                      'h-[36px] rounded-full px-4 text-[13px] font-semibold outline-none',
                      'transition-[background-color,color,box-shadow] duration-[160ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                    )}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 mb-0 text-[12px] text-mute">{LENGTHS.find((l) => l.id === sessionLength)!.note}</p>
          </div>

          {/* ---- mode, region, size ---- */}
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Game mode">
              {game?.modes?.length ? (
                <Select
                  value={effectiveMode}
                  onChange={setMode}
                  options={game.modes.map((m) => ({ value: m, label: m }))}
                />
              ) : (
                <span className="flex h-[var(--h-md)] items-center rounded-[calc(12px*var(--radius-mul,1))] border border-dashed border-line px-3 text-[13px] text-ghost">
                  {game ? 'No modes for this game' : 'Pick a game first'}
                </span>
              )}
            </Field>
            <Field label="Region">
              <Select
                lead="globe"
                value={region}
                onChange={setRegion}
                options={regions.map((r) => ({ value: r.id, label: r.name }))}
              />
            </Field>
            <Field label="Party size">
              <NumberField value={size} min={2} max={cap} onChange={setSize} suffix="players" />
            </Field>
          </div>
          <p className="-mt-2 m-0 text-[12.5px] leading-relaxed text-mute">
            {game?.squad ? game.name + ' is usually played ' + game.squad + ' a side. ' : ''}
            Up to {cap} players on your plan.
            {cap > 10 ? ' Most games play in squads of 2 to 10 — larger sizes suit social lobbies.' : ''}
          </p>

          {/* ---- visibility ---- */}
          <div>
            <span id="cp-visibility" className="mb-2 block text-[13.5px] font-semibold text-ink">Who can see it</span>
            <div
              role="radiogroup"
              aria-labelledby="cp-visibility"
              className="grid grid-cols-3 gap-1 rounded-[calc(12px*var(--radius-mul,1))] border border-line-soft bg-surface-inset p-1"
              onKeyDown={(e) => {
                if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
                e.preventDefault();
                const i = VISIBILITY.findIndex((v) => v.id === visibility);
                const next = VISIBILITY[(i + (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1) + VISIBILITY.length) % VISIBILITY.length]!;
                setVisibility(next.id);
                (e.currentTarget.querySelector('[data-vis="' + next.id + '"]') as HTMLElement | null)?.focus();
              }}
            >
              {VISIBILITY.map((v) => {
                const on = v.id === visibility;
                return (
                  <button
                    key={v.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-vis={v.id}
                    tabIndex={on ? 0 : -1}
                    onClick={() => setVisibility(v.id)}
                    className={cn(
                      'h-[38px] rounded-[calc(10px*var(--radius-mul,1))] px-2 text-[13.5px] font-semibold outline-none',
                      'transition-[background-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                      'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-surface-1 text-accent shadow-[var(--shadow-1)]' : 'text-dim hover:text-ink'
                    )}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 mb-0 text-[12.5px] text-mute">{VISIBILITY.find((v) => v.id === visibility)!.explain}</p>
          </div>

          {/* ---- platform ---- */}
          <div>
            <span id="cp-platform" className="mb-2 block text-[13.5px] font-semibold text-ink">Playing on</span>
            <div role="group" aria-labelledby="cp-platform" className="flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => {
                const on = platforms.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setPlatforms((list) => on ? list.filter((x) => x !== p.id) : [...list, p.id])}
                    className={cn(
                      'flex h-[34px] items-center gap-1.5 rounded-full px-3.5 text-[12.5px] font-semibold outline-none',
                      'transition-colors duration-[var(--t-hover)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                      on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                    )}
                  >
                    <Icon name={p.icon} size={14} />
                    {p.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 mb-0 text-[12px] text-mute">
              {platforms.length ? 'Shown on the card, so nobody joins a lobby they cannot get into.' : 'Leave it blank if it does not matter.'}
            </p>
          </div>

          {/* ---- microphone ---- */}
          <Toggle
            checked={micRequired}
            onChange={setMicRequired}
            label="Microphone required"
            note="Everyone sees this before they join. It never turns anyone's microphone on."
          />
          <Toggle
            checked={newcomers}
            onChange={setNewcomers}
            label="New players welcome"
            note="Says plainly that somebody who has never played this is welcome here. It is what beginners search for."
          />

          {/* ---- more ---- */}
          <div className="border-t border-line-soft pt-4">
            <button
              type="button"
              aria-expanded={more}
              aria-controls="cp-more"
              onClick={() => setMore((v) => !v)}
              className="flex items-center gap-1.5 rounded-[8px] px-1 py-1 text-[13.5px] font-semibold text-dim outline-none transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true" className={cn('transition-transform duration-[var(--t-hover)] ease-[var(--ease)]', more && 'rotate-90')}><path d="m9 6 6 6-6 6" /></svg>
              More options
              <span className="font-normal text-mute">— {moreSummary}</span>
            </button>

            {more ? (
              <div id="cp-more" className="mt-4 flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Language">
                    <Select
                      value={language}
                      onChange={setLanguage}
                      options={(catalog?.languages ?? ['English']).map((l) => ({ value: l, label: l }))}
                    />
                  </Field>
                  <div>
                    <span id="cp-styles" className="mb-2 block text-[13.5px] font-semibold text-ink">Playstyle</span>
                    <div role="group" aria-labelledby="cp-styles" className="flex flex-wrap gap-1.5">
                      {(catalog?.playstyles ?? []).map((ps) => {
                        const on = styles.includes(ps.name);
                        const full = !on && styles.length >= MAX_STYLES;
                        return (
                          <button
                            key={ps.id}
                            type="button"
                            aria-pressed={on}
                            disabled={full}
                            onClick={() => setStyles((cur) => on ? cur.filter((x) => x !== ps.name) : [...cur, ps.name])}
                            className={cn(
                              'h-[32px] rounded-full border px-3 text-[12.5px] font-semibold outline-none',
                              'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                              'focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-40',
                              on ? 'border-accent-line bg-accent-soft text-accent' : 'border-line-soft bg-surface-2 text-dim hover:border-line hover:text-ink'
                            )}
                          >
                            {ps.name}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1.5 mb-0 text-[12px] text-mute">Up to {MAX_STYLES}.</p>
                  </div>
                </div>

                {ranks.length ? (
                  <div>
                    <span id="cp-rank" className="mb-2 block text-[13.5px] font-semibold text-ink">Rank range</span>
                    <div role="group" aria-labelledby="cp-rank" className="grid gap-4 sm:grid-cols-2">
                      <Select
                        lead="chart"
                        aria-label="Lowest rank"
                        value={minRank}
                        onChange={(v) => {
                          setMinRank(v);
                          /* A floor above the ceiling is not a range anybody meant. */
                          if (v && maxRank && ranks.indexOf(v) > ranks.indexOf(maxRank)) setMaxRank(v);
                        }}
                        options={[{ value: '', label: 'Any rank' }, ...ranks.map((r) => ({ value: r, label: 'From ' + r }))]}
                      />
                      <Select
                        lead="chart"
                        aria-label="Highest rank"
                        value={maxRank}
                        onChange={(v) => {
                          setMaxRank(v);
                          if (v && minRank && ranks.indexOf(v) < ranks.indexOf(minRank)) setMinRank(v);
                        }}
                        options={[{ value: '', label: 'Any rank' }, ...ranks.map((r) => ({ value: r, label: 'Up to ' + r }))]}
                      />
                    </div>
                    <p className="mt-1.5 mb-0 text-[12px] text-mute">
                      {rankRange ? 'Shown on the card. Nobody is stopped from joining.' : 'Leave both on Any to welcome every rank.'}
                    </p>
                  </div>
                ) : null}

                {roles.length ? (
                  <div>
                    <span id="cp-roles" className="mb-2 block text-[13.5px] font-semibold text-ink">Roles you need</span>
                    <div role="group" aria-labelledby="cp-roles" className="flex flex-wrap gap-1.5">
                      {roles.map((role) => {
                        const on = needRoles.includes(role);
                        const full = !on && needRoles.length >= MAX_ROLES;
                        return (
                          <button
                            key={role}
                            type="button"
                            aria-pressed={on}
                            disabled={full}
                            onClick={() => setNeedRoles((cur) => on ? cur.filter((x) => x !== role) : [...cur, role])}
                            className={cn(
                              'h-[32px] rounded-full border px-3 text-[12.5px] font-semibold outline-none',
                              'transition-[background-color,border-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                              'focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-40',
                              on ? 'border-accent-line bg-accent-soft text-accent' : 'border-line-soft bg-surface-2 text-dim hover:border-line hover:text-ink'
                            )}
                          >
                            {role}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1.5 mb-0 text-[12px] text-mute">Up to {MAX_ROLES}. People see what the squad is missing.</p>
                  </div>
                ) : null}

                <div className="grid gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-inset p-3">
                  <Toggle
                    checked={voice}
                    onChange={setVoice}
                    label="Voice room"
                    note="A room to talk in, open from the moment the party exists."
                  />
                  <Toggle
                    checked={textEnabled}
                    onChange={setTextEnabled}
                    label="Party chat"
                    note="Off makes it a voice-only party. You can turn it back on later."
                  />
                </div>

                <Field label="Description" hint={(description.length ? description.length + '/500 · ' : '') + 'Ranks, roles, the vibe you want.'}>
                  <EmojiField
                    textarea
                    rows={3}
                    value={description}
                    onChange={setDescription}
                    maxLength={500}
                    placeholder="Chill games, good comms, no rage."
                  />
                </Field>
              </div>
            ) : null}
          </div>
        </div>

        {/* ============================ preview ============================ */}
        <aside aria-label="Preview of your party card" className="min-w-0 md:sticky md:top-0 md:self-start">
          <p className="m-0 mb-2 text-[13px] font-semibold text-mute">Live preview</p>
          <div className="overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-2">
            <div className="aspect-[16/10] bg-surface-3">
              {game && gameBanner(game.id)
                ? <img src={gameBanner(game.id)!} alt="" className="h-full w-full object-cover" />
                : <span className="grid h-full place-items-center text-[13px] text-ghost">{game ? game.short : 'No game yet'}</span>}
            </div>
            <div className="flex flex-col gap-2 p-3.5">
              <p className="m-0 line-clamp-2 text-[15px] leading-snug font-bold text-ink">
                {title.trim() || (game ? game.name + ' squad' : 'Your party')}
              </p>
              <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[12.5px] text-dim">
                <li className="flex items-center gap-2"><Icon name="dice" size={14} className="flex-none text-mute" />{game ? game.name + (effectiveMode ? ' · ' + effectiveMode : '') : 'Pick a game'}</li>
                <li className="flex items-center gap-2"><Icon name="users" size={14} className="flex-none text-mute" />1 / {size} players{region ? ' · ' + (regions.find((r) => r.id === region)?.name ?? region) : ''}</li>
                {newcomers ? <li className="flex items-center gap-2"><Icon name="heart" size={14} className="flex-none text-mute" />New players welcome</li> : null}
                {platforms.length ? <li className="flex items-center gap-2"><Icon name="monitor" size={14} className="flex-none text-mute" />{platforms.map((p) => PLATFORMS.find((x) => x.id === p)?.label ?? p).join(', ')}</li> : null}
                {sessionLength ? <li className="flex items-center gap-2"><Icon name="clock" size={14} className="flex-none text-mute" />{LENGTHS.find((l) => l.id === sessionLength)!.label}</li> : null}
                {rankRange ? <li className="flex items-center gap-2"><Icon name="chart" size={14} className="flex-none text-mute" />{rankRange}</li> : null}
                {needRoles.length ? <li className="flex items-center gap-2"><Icon name="users" size={14} className="flex-none text-mute" />Need {needRoles.join(', ')}</li> : null}
                {micRequired ? <li className="flex items-center gap-2"><Icon name="mic" size={14} className="flex-none text-mute" />Mic required</li> : null}
                {voice ? null : <li className="flex items-center gap-2"><Icon name="mic" size={14} className="flex-none text-mute" />No voice room</li>}
                {textEnabled ? null : <li className="flex items-center gap-2"><Icon name="chat" size={14} className="flex-none text-mute" />No party chat</li>}
                <li className="flex items-center gap-2"><Icon name="globe" size={14} className="flex-none text-mute" />{VISIBILITY.find((v) => v.id === visibility)!.label}</li>
              </ul>
              {styles.length || more ? (
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-full bg-surface-3 px-2.5 py-[3px] text-[11.5px] font-semibold text-dim">{language}</span>
                  {styles.map((st) => <span key={st} className="rounded-full bg-surface-3 px-2.5 py-[3px] text-[11.5px] font-semibold text-dim">{st}</span>)}
                </div>
              ) : null}
              {description.trim() ? <p className="m-0 line-clamp-3 text-[12.5px] text-mute">{description.trim()}</p> : null}
            </div>
          </div>
        </aside>
      </div>
    </Modal>
  );
}
